import uuid from 'uuid';
import { HttpClient } from '@packages/core/http';
import { ConfigService, Constants } from '@packages/core/config';
import { PushNotificationsService as AbstractPushNotificationsService } from '@packages/core/push-notifications';
import { getLogger } from '@packages/core/logging';
import { StorageService, WebStorageKeys } from '@packages/core/storage';
import * as firebase from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';

const logger = getLogger('push-notifications');

export class PushNotificationsService extends AbstractPushNotificationsService {
    protected deviceFingerprint = uuid();
    protected clientDeviceTypeId = 'WEB_BROWSER';

    private firebaseConfig: {
        apiKey?: string;
        projectId?: string;
        messagingSenderId?: string;
        appId?: string;
    };

    private didRegisterServiceWorker = false;

    constructor(storageService: StorageService, configService: ConfigService, http: HttpClient) {
        super(http);

        storageService.getItem(WebStorageKeys.BrowserDeviceId).then((deviceFingerprint) => {
            logger.debug('Resolved the device fingerprint', {
                deviceFingerprint: deviceFingerprint || this.deviceFingerprint,
            });

            if (!deviceFingerprint) {
                return storageService.setItem(WebStorageKeys.BrowserDeviceId, this.deviceFingerprint);
            }

            this.deviceFingerprint = deviceFingerprint;
        });

        const vapidKey = configService.get(Constants.Env.FirebaseVapidKey);
        this.firebaseConfig = {
            apiKey: configService.get(Constants.Env.FirebaseApiKey),
            projectId: configService.get(Constants.Env.FirebaseProjectId),
            messagingSenderId: configService.get(Constants.Env.FirebaseMessagingSenderId),
            appId: configService.get(Constants.Env.FirebaseAppId),
        };

        try {
            const firebaseApp = firebase.initializeApp(this.firebaseConfig);

            const messaging = getMessaging();

            logger.debug('Resolved the VAPID key from config', { hasVapidKey: Boolean(vapidKey) });
            if (vapidKey) {
                getToken(messaging, { vapidKey }).catch((error) => {
                    logger.error('Failed to get or register the push token', { error });
                });
            }
        } catch (e) {
            logger.warn('Failed to initialize messaging', { error: e });
        }
    }

    registerServiceWorker() {
        logger.debug('Registering the messaging service worker');
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker
                // the source file lives in the `public/` folder
                // path here is relative to the final `build/static/js` folder
                .register('../../firebase-messaging-sw.js')
                .then(() => {
                    this.didRegisterServiceWorker = true;
                    logger.info('Messaging service worker registered');
                })
                .catch((e) => {
                    logger.error('Failed to register the Firebase Messaging service worker', { error: e });
                });
        }
    }

    async init() {
        if (!this.didRegisterServiceWorker) {
            logger.warn('Failed to init push notifications - messaging is not available');
            return;
        }

        const messaging = getMessaging();

        try {
            // Even "data-only" messages _require_ permissions
            // unlike mobile, we can't get a token on Web without permissions.

            // await messaging.requestPermission();

            const getAndRegisterToken = async () => {
                try {
                    getToken(messaging)
                        .then((pushToken) => {
                            return this.registerDeviceToken(pushToken);
                        })
                        .catch((error) => {
                            logger.error('Failed to get or register the push token', { error });
                        });

                    // const pushToken = await getToken(messaging);
                    // await this.registerDeviceToken(pushToken);
                } catch (error) {
                    logger.error('Failed to get or register the push token', { error });
                }
            };

            // this.listenerTeardown.onTokenRefresh = messaging.onTokenRefresh(getAndRegisterToken);
            this.listenerTeardown.onLogout = () => {
                try {
                    deleteToken(messaging);
                } catch (error) {
                    if (error.code === 'messaging/token-unsubscribe-failed') {
                        logger.warn('Failed to clear the push token', { error });
                        return;
                    }

                    throw error;
                }
            };

            await getAndRegisterToken();

            // TODO: Message handling probably needs to move into
            // a UI/React hook on pages/components interested in
            // receiving & processing incoming messages.
            // This is only here for testing/debugging

            const foregroundOnMessageTeardown = onMessage(messaging, (m: any) => {
                logger.debug('Received a Firebase foreground message', { messageId: m?.messageId });
            });

            const onMessageDebug = (m: any) => {
                logger.debug('Received a background-posted message from the service worker', {
                    messageId: m?.data?.messageId,
                });
            };

            navigator.serviceWorker.addEventListener('message', onMessageDebug);
            this.listenerTeardown.onMessage = () => {
                foregroundOnMessageTeardown();
                navigator.serviceWorker.removeEventListener('message', onMessageDebug);
            };
        } catch (error) {
            if (
                error.code === 'messaging/permission-blocked' ||
                error.customData.serverStatus === 'PERMISSION_DENIED'
            ) {
                logger.warn('The notification permission was not granted and blocked instead', { error });
            } else {
                logger.error('Failed to start listening for notifications', { error });
            }
        }
    }
}
